"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useMemo } from "react";
import PrimaryButton from "@/components/utils/button/PrimaryButton";
import InfoButton from "@/components/utils/button/InfoButton";
import ClosedButton from "@/components/utils/button/ClosedButton";
import ClockIcon from "@/components/utils/icons/ClockIcon";
import CalendarClosedIcon from "@/components/utils/icons/CalendarClosedIcon";
import LimitIcon from "@/components/utils/icons/LimitIcon";
import PermissionIcon from "@/components/utils/icons/PermissionIcon";
import MainStageNominationPopover from "./MainStageNominationPopover";
import type { ApiWave } from "@/generated/models/ApiWave";
import { useWave, SubmissionStatus } from "@/hooks/useWave";
import { useCountdown } from "@/hooks/useCountdown";
interface MyStreamWaveTabsMemeSubmitProps {
  readonly wave: ApiWave;
  readonly handleMemesSubmit: () => void;
}
const HEADER_ACTION_BUTTON_CLASS = "tw-max-w-[11.5rem] tw-whitespace-nowrap tw-text-xs lg:tw-max-w-[13.5rem] xl:tw-max-w-none";
const HEADER_ACTION_BUTTON_TEXT_CLASS = "tw-min-w-0 tw-truncate";
const NOT_ELIGIBLE_BUTTON_CLASS = `tw-group tw-flex tw-w-auto tw-cursor-pointer tw-items-center tw-justify-center tw-gap-x-1.5 tw-rounded-lg tw-border tw-border-solid tw-border-[#2a2c32] tw-bg-[#181a1d] tw-px-2.5 tw-py-2 tw-text-xs tw-font-semibold tw-text-[#d6d8dd] tw-transition-colors tw-duration-[180ms] tw-ease-out hover:tw-border-[#3a3d45] hover:tw-bg-[#202329] hover:tw-text-[#f2f3f5] focus-visible:tw-outline focus-visible:tw-outline-2 focus-visible:tw-outline-offset-2 focus-visible:tw-outline-primary-500 ${HEADER_ACTION_BUTTON_CLASS}`;
const ACTIVE_LABEL_SUBMIT_MEME = "Submit Meme";
const getActiveLabels = ({
  canSubmit,
  isEndingVerySoon,
  isEndingHighlyUrgent,
  isEndingSoon
}: {
  canSubmit: boolean;
  isEndingVerySoon: boolean;
  isEndingHighlyUrgent: boolean;
  isEndingSoon: boolean;
}): {
  activeLabelFull: string;
  activeLabelCompact: string;
} => {
  if (!canSubmit) {
    return {
      activeLabelFull: "Submit Work to The Memes",
      activeLabelCompact: "Submit Work"
    };
  }
  if (isEndingVerySoon) {
    return {
      activeLabelFull: ACTIVE_LABEL_SUBMIT_MEME,
      activeLabelCompact: ACTIVE_LABEL_SUBMIT_MEME
    };
  }
  if (isEndingHighlyUrgent) {
    return {
      activeLabelFull: ACTIVE_LABEL_SUBMIT_MEME,
      activeLabelCompact: ACTIVE_LABEL_SUBMIT_MEME
    };
  }
  if (isEndingSoon) {
    return {
      activeLabelFull: "Submit Meme (Closes Soon!)",
      activeLabelCompact: ACTIVE_LABEL_SUBMIT_MEME
    };
  }
  return {
    activeLabelFull: "Submit Work to The Memes",
    activeLabelCompact: "Submit Work"
  };
};

/**
 * Button component for The Memes submissions with multiple states:
 * - Coming soon (blue info button with countdown)
 * - Active (green primary button)
 *   - With submission count indicator when limited submissions remaining
 *   - With urgency indicator when submission period is ending soon
 * - Maximum submissions reached (blue info button, disabled)
 * - Not eligible (interactive nomination progress popover)
 * - Closed (gray disabled button)
 *
 * Uses useWave hook to determine submission status, eligibility,
 * time windows, and submission limits
 */
const MyStreamWaveTabsMemeSubmit: React.FC<MyStreamWaveTabsMemeSubmitProps> = t0 => {
  const $ = _c(69);
  const {
    wave,
    handleMemesSubmit
  } = t0;
  const waveInfo = useWave(wave);
  const targetTime = waveInfo?.participation.status === SubmissionStatus.NOT_STARTED ? waveInfo?.participation.startTime : null;
  const countdown = useCountdown(targetTime);
  const endTimeForCountdown = waveInfo?.participation.endTime ?? null;
  const endingCountdown = useCountdown(endTimeForCountdown);
  waveInfo?.participation.canSubmitNow;
  const canSubmit = waveInfo?.participation.canSubmitNow ?? false;
  if (!waveInfo) {
    return null;
  }
  const submissionStatus = waveInfo.participation.status;
  const isClosed = submissionStatus === SubmissionStatus.ENDED;
  const isComingSoon = submissionStatus === SubmissionStatus.NOT_STARTED;
  const openingTime = waveInfo.participation.startTime;
  const closingTime = waveInfo.participation.endTime;
  if (isClosed) {
    let t1;
    if ($[0] !== closingTime) {
      t1 = closingTime ? `Submissions closed on ${new Date(closingTime).toLocaleString()}` : "Submissions are closed";
      $[0] = closingTime;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    const tooltipText = t1;
    let t2;
    if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <CalendarClosedIcon className="tw-h-5 tw-w-5 tw-flex-shrink-0" />;
      $[2] = t2;
    } else {
      t2 = $[2];
    }
    let t3;
    if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = <span className={HEADER_ACTION_BUTTON_TEXT_CLASS}><span className="xl:tw-hidden">Closed</span><span className="tw-hidden xl:tw-inline">Submissions Closed</span></span>;
      $[3] = t3;
    } else {
      t3 = $[3];
    }
    let t4;
    if ($[4] !== tooltipText) {
      t4 = <ClosedButton title={tooltipText} fullWidth={false} className={HEADER_ACTION_BUTTON_CLASS}>{t2}{t3}</ClosedButton>;
      $[4] = tooltipText;
      $[5] = t4;
    } else {
      t4 = $[5];
    }
    return t4;
  }
  if (isComingSoon) {
    let t1;
    if ($[6] !== openingTime) {
      t1 = openingTime ? `Submissions open on ${new Date(openingTime).toLocaleString()}` : "Submissions will open soon";
      $[6] = openingTime;
      $[7] = t1;
    } else {
      t1 = $[7];
    }
    const tooltipText_0 = t1;
    let t2;
    if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <ClockIcon className="tw-h-5 tw-w-5 tw-flex-shrink-0" />;
      $[8] = t2;
    } else {
      t2 = $[8];
    }
    let t3;
    if ($[9] !== countdown) {
      t3 = <span className={HEADER_ACTION_BUTTON_TEXT_CLASS}><span className="xl:tw-hidden">Opens {countdown}</span><span className="tw-hidden xl:tw-inline">Submissions Open {countdown}</span></span>;
      $[9] = countdown;
      $[10] = t3;
    } else {
      t3 = $[10];
    }
    let t4;
    if ($[11] !== t3 || $[12] !== tooltipText_0) {
      t4 = <InfoButton disabled={true} title={tooltipText_0} variant="info" fullWidth={false} className={HEADER_ACTION_BUTTON_CLASS}>{t2}{t3}</InfoButton>;
      $[11] = t3;
      $[12] = tooltipText_0;
      $[13] = t4;
    } else {
      t4 = $[13];
    }
    return t4;
  }
  if (waveInfo.participation.isEligible === false) {
    let t1;
    if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <MainStageNominationPopover><button type="button" aria-haspopup="dialog" className={NOT_ELIGIBLE_BUTTON_CLASS}><PermissionIcon className="tw-h-5 tw-w-5 tw-flex-shrink-0 tw-opacity-70 tw-transition-opacity tw-duration-[180ms] tw-ease-out group-hover:tw-opacity-100" /><span className={HEADER_ACTION_BUTTON_TEXT_CLASS}>How to Submit</span></button></MainStageNominationPopover>;
      $[14] = t1;
    } else {
      t1 = $[14];
    }
    return t1;
  }
  if (waveInfo.participation.hasReachedLimit) {
    const maxSubmissions = waveInfo.participation.maxSubmissions ?? "?";
    const submissionText = maxSubmissions === 1 ? "1 submission" : `${maxSubmissions} submissions`;
    const tooltipText_1 = `You have already submitted the maximum allowed (${submissionText})`;
    let t1;
    if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <LimitIcon className="tw-h-5 tw-w-5 tw-flex-shrink-0" />;
      $[15] = t1;
    } else {
      t1 = $[15];
    }
    let t2;
    if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <span className="xl:tw-hidden">Limit Reached</span>;
      $[16] = t2;
    } else {
      t2 = $[16];
    }
    const t3 = maxSubmissions === 1 ? "Submission Limit Reached (1)" : `Submission Limit Reached (${maxSubmissions})`;
    let t4;
    if ($[17] !== t3) {
      t4 = <span className={HEADER_ACTION_BUTTON_TEXT_CLASS}>{t2}<span className="tw-hidden xl:tw-inline">{t3}</span></span>;
      $[17] = t3;
      $[18] = t4;
    } else {
      t4 = $[18];
    }
    let t5;
    if ($[19] !== t4 || $[20] !== tooltipText_1) {
      t5 = <InfoButton disabled={true} title={tooltipText_1} variant="muted" fullWidth={false} className={HEADER_ACTION_BUTTON_CLASS}>{t1}{t4}</InfoButton>;
      $[19] = t4;
      $[20] = tooltipText_1;
      $[21] = t5;
    } else {
      t5 = $[21];
    }
    return t5;
  }
  const hasLimitedRemaining = waveInfo.participation.remainingSubmissions !== null && waveInfo.participation.remainingSubmissions <= 3 && waveInfo.participation.remainingSubmissions > 0;
  const now = Date.now();
  const endTime = waveInfo.participation.endTime;
  const timeRemaining = endTime ? endTime - now : null;
  const isEndingVerySoon = waveInfo.participation.isWithinPeriod && timeRemaining !== null && timeRemaining < 3600000 && timeRemaining > 0;
  const isEndingHighlyUrgent = waveInfo.participation.isWithinPeriod && timeRemaining !== null && timeRemaining < 21600000 && timeRemaining > 0;
  const isEndingSoon = waveInfo.participation.isWithinPeriod && timeRemaining !== null && timeRemaining < 86400000 && timeRemaining > 0;
  let t1;
  if ($[22] !== canSubmit || $[23] !== endTime || $[24] !== hasLimitedRemaining || $[25] !== isEndingHighlyUrgent || $[26] !== isEndingSoon || $[27] !== isEndingVerySoon || $[28] !== waveInfo.participation.remainingSubmissions) {
    t1 = canSubmit ? hasLimitedRemaining ? `You have ${waveInfo.participation.remainingSubmissions} submission${waveInfo.participation.remainingSubmissions === 1 ? "" : "s"} remaining` : isEndingVerySoon ? `URGENT: Submissions closing in less than an hour! Ends at ${new Date(endTime).toLocaleString()}` : isEndingHighlyUrgent ? `Submissions closing in a few hours! Ends at ${new Date(endTime).toLocaleString()}` : isEndingSoon ? `Submissions closing soon! Ends on ${new Date(endTime).toLocaleString()}` : "Submit your art for The Memes" : "You cannot submit at this time";
    $[22] = canSubmit;
    $[23] = endTime;
    $[24] = hasLimitedRemaining;
    $[25] = isEndingHighlyUrgent;
    $[26] = isEndingSoon;
    $[27] = isEndingVerySoon;
    $[28] = waveInfo.participation.remainingSubmissions;
    $[29] = t1;
  } else {
    t1 = $[29];
  }
  const tooltipText_2 = t1;
  let t2;
  if ($[30] !== canSubmit || $[31] !== isEndingHighlyUrgent || $[32] !== isEndingSoon || $[33] !== isEndingVerySoon) {
    t2 = getActiveLabels({
      canSubmit,
      isEndingVerySoon,
      isEndingHighlyUrgent,
      isEndingSoon
    });
    $[30] = canSubmit;
    $[31] = isEndingHighlyUrgent;
    $[32] = isEndingSoon;
    $[33] = isEndingVerySoon;
    $[34] = t2;
  } else {
    t2 = $[34];
  }
  const {
    activeLabelFull,
    activeLabelCompact
  } = t2;
  const showSplitActiveLabels = activeLabelCompact !== activeLabelFull;
  const t3 = !canSubmit;
  let t4;
  if ($[35] !== canSubmit || $[36] !== isEndingHighlyUrgent || $[37] !== isEndingSoon || $[38] !== isEndingVerySoon) {
    t4 = isEndingVerySoon && canSubmit ? <ClockIcon className="tw-text-red-500 tw-h-5 tw-w-5 tw-flex-shrink-0 tw-animate-pulse" /> : isEndingHighlyUrgent && canSubmit ? <ClockIcon className="tw-h-5 tw-w-5 tw-flex-shrink-0 tw-animate-pulse tw-text-amber-500" /> : isEndingSoon && canSubmit ? <ClockIcon className="tw-h-5 tw-w-5 tw-flex-shrink-0 tw-text-amber-400" /> : <svg className="tw-h-5 tw-w-5 tw-flex-shrink-0" viewBox="0 0 24 24" fill="none" aria-hidden="true" xmlns="http://www.w3.org/2000/svg"><path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /><path d="M7.5 12H16.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /><path d="M12 7.5V16.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>;
    $[35] = canSubmit;
    $[36] = isEndingHighlyUrgent;
    $[37] = isEndingSoon;
    $[38] = isEndingVerySoon;
    $[39] = t4;
  } else {
    t4 = $[39];
  }
  let t5;
  if ($[40] !== activeLabelCompact || $[41] !== activeLabelFull || $[42] !== showSplitActiveLabels) {
    t5 = showSplitActiveLabels ? <><span className="xl:tw-hidden">{activeLabelCompact}</span><span className="tw-hidden xl:tw-inline">{activeLabelFull}</span></> : <span>{activeLabelFull}</span>;
    $[40] = activeLabelCompact;
    $[41] = activeLabelFull;
    $[42] = showSplitActiveLabels;
    $[43] = t5;
  } else {
    t5 = $[43];
  }
  let t6;
  if ($[44] !== canSubmit || $[45] !== endingCountdown || $[46] !== isEndingVerySoon) {
    t6 = isEndingVerySoon && canSubmit && <span className="tw-bg-red-600 tw-hidden tw-animate-pulse tw-items-center tw-rounded-full tw-border tw-border-white/30 tw-px-1.5 tw-py-0.5 tw-text-xs tw-font-medium tw-text-white xl:tw-inline-flex">Closing {endingCountdown}!</span>;
    $[44] = canSubmit;
    $[45] = endingCountdown;
    $[46] = isEndingVerySoon;
    $[47] = t6;
  } else {
    t6 = $[47];
  }
  let t7;
  if ($[48] !== canSubmit || $[49] !== endingCountdown || $[50] !== isEndingHighlyUrgent || $[51] !== isEndingVerySoon) {
    t7 = isEndingHighlyUrgent && !isEndingVerySoon && canSubmit && <span className="tw-hidden tw-items-center tw-rounded-full tw-border tw-border-white/30 tw-bg-amber-600 tw-px-1.5 tw-py-0.5 tw-text-xs tw-font-medium tw-text-white xl:tw-inline-flex">Closing {endingCountdown}</span>;
    $[48] = canSubmit;
    $[49] = endingCountdown;
    $[50] = isEndingHighlyUrgent;
    $[51] = isEndingVerySoon;
    $[52] = t7;
  } else {
    t7 = $[52];
  }
  let t8;
  if ($[53] !== canSubmit || $[54] !== hasLimitedRemaining || $[55] !== isEndingVerySoon || $[56] !== waveInfo.participation.remainingSubmissions) {
    t8 = hasLimitedRemaining && !isEndingVerySoon && canSubmit && <span className="tw-hidden tw-items-center tw-rounded-full tw-border tw-border-white/30 tw-bg-primary-600 tw-px-1.5 tw-py-0.5 tw-text-xs tw-font-medium tw-text-white xl:tw-inline-flex">{waveInfo.participation.remainingSubmissions === 1 ? "Last One!" : `${waveInfo.participation.remainingSubmissions} Left`}</span>;
    $[53] = canSubmit;
    $[54] = hasLimitedRemaining;
    $[55] = isEndingVerySoon;
    $[56] = waveInfo.participation.remainingSubmissions;
    $[57] = t8;
  } else {
    t8 = $[57];
  }
  let t9;
  if ($[58] !== t5 || $[59] !== t6 || $[60] !== t7 || $[61] !== t8) {
    t9 = <div className="tw-flex tw-min-w-0 tw-items-center tw-gap-2 tw-whitespace-nowrap">{t5}{t6}{t7}{t8}</div>;
    $[58] = t5;
    $[59] = t6;
    $[60] = t7;
    $[61] = t8;
    $[62] = t9;
  } else {
    t9 = $[62];
  }
  let t10;
  if ($[63] !== handleMemesSubmit || $[64] !== t3 || $[65] !== t4 || $[66] !== t9 || $[67] !== tooltipText_2) {
    t10 = <PrimaryButton loading={false} disabled={t3} onClicked={handleMemesSubmit} size="sm" title={tooltipText_2}>{t4}{t9}</PrimaryButton>;
    $[63] = handleMemesSubmit;
    $[64] = t3;
    $[65] = t4;
    $[66] = t9;
    $[67] = tooltipText_2;
    $[68] = t10;
  } else {
    t10 = $[68];
  }
  return t10;
};
export default MyStreamWaveTabsMemeSubmit;